import { useState, useEffect } from 'react'
import { SettingsIcon, Save, Edit, Trash2, Users, UserPlus, Mail, Edit3, Send } from 'lucide-react'
import BulkActionsModal from '@/components/BulkActionsModal'
import ApiKeysCard from '@/components/ApiKeysCard'
import ConnecteurCard from '@/components/ConnecteurCard'
import UpSpotCard from '@/components/UpSpotCard'
import { useAuthStore } from '@/store/authStore'
import { Button } from '@/components/ui/button'
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card'
import { Label } from '@/components/ui/label'
import { Input } from '@/components/ui/input'
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from '@/components/ui/select'
import { Table, TableBody, TableCell, TableHead, TableHeader, TableRow } from '@/components/ui/table'
import { Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle, DialogTrigger } from '@/components/ui/dialog'
import { Badge } from '@/components/ui/badge'
import { useToast } from '@/hooks/use-toast'
import { apiService, Setting, UserForList, UserCreateInput, UserUpdateInput } from '@/services/api'
import { isValidEmail, normalizeEmail } from '@/utils/validation'

export default function Settings() {
  const estAdmin = Boolean(useAuthStore((etat) => etat.user?.is_admin))
  // États pour les paramètres généraux
  const [elementsPerPage, setElementsPerPage] = useState({
    dashboard: '5',
    contacts: '15',
    companies: '15'
  })
  const [loading, setLoading] = useState(true)
  const [saving, setSaving] = useState(false)

  // États pour la gestion des utilisateurs
  const [users, setUsers] = useState<UserForList[]>([])
  const [loadingUsers, setLoadingUsers] = useState(false)
  const [isCreateUserModalOpen, setIsCreateUserModalOpen] = useState(false)
  const [isEditUserModalOpen, setIsEditUserModalOpen] = useState(false)
  const [isDeleteUserModalOpen, setIsDeleteUserModalOpen] = useState(false)
  const [selectedUser, setSelectedUser] = useState<UserForList | null>(null)
  const [createUserForm, setCreateUserForm] = useState<UserCreateInput>({
    username: '',
    is_admin: false
  })
  const [editUserForm, setEditUserForm] = useState<UserUpdateInput & { password?: string }>({
    username: '',
    is_admin: false,
    password: ''
  })
  const [savingUser, setSavingUser] = useState(false)
  const [deletingUser, setDeletingUser] = useState(false)
  const [invitationEnCours, setInvitationEnCours] = useState<string | null>(null)

  // Actions multiples (modal)
  const [bulkOpen, setBulkOpen] = useState(false)

  const { toast } = useToast()

  // Charger les paramètres depuis l'API
  useEffect(() => {
    const loadSettings = async () => {
      try {
        setLoading(true)
        const response = await apiService.getSettings()
        
        if (response.success && response.data) {
          const setting = response.data as Setting
          
          setElementsPerPage({
            dashboard: setting.elementsPerPageDashboard.toString(),
            contacts: setting.elementsPerPageContacts.toString(),
            companies: setting.elementsPerPageCompanies.toString()
          })
        }
      } catch (error) {
        console.error('Erreur lors du chargement des paramètres:', error)
        toast({
          title: "Erreur",
          description: "Impossible de charger les paramètres",
          variant: "destructive",
        })
      } finally {
        setLoading(false)
      }
    }

    loadSettings()
  }, [toast])

  // Charger les utilisateurs
  const loadUsers = async () => {
    try {
      setLoadingUsers(true)
      const response = await apiService.getUsers()
      
      if (response.success && response.data) {
        setUsers(response.data.data)
      } else {
        toast({
          title: "Erreur",
          description: "Impossible de charger les utilisateurs",
          variant: "destructive",
        })
      }
    } catch (error) {
      console.error('Erreur lors du chargement des utilisateurs:', error)
      toast({
        title: "Erreur",
        description: "Erreur lors du chargement des utilisateurs",
        variant: "destructive",
      })
    } finally {
      setLoadingUsers(false)
    }
  }

  // Charger les utilisateurs au montage
  useEffect(() => {
    loadUsers()
  }, [])

  // Fonctions pour la gestion des utilisateurs
  const handleCreateUser = async () => {
    if (!createUserForm.username) {
      toast({
        title: "Erreur de validation",
        description: "L'adresse email est requise",
        variant: "destructive",
      })
      return
    }

    if (!isValidEmail(createUserForm.username)) {
      toast({
        title: "Erreur de validation",
        description: "Veuillez entrer une adresse email valide",
        variant: "destructive",
      })
      return
    }

    try {
      setSavingUser(true)
      const userDataWithNormalizedEmail = {
        ...createUserForm,
        username: normalizeEmail(createUserForm.username)
      }
      const response = await apiService.createUser(userDataWithNormalizedEmail)
      
      if (response.success) {
        if (response.data?.invitationEnvoyee) {
          toast({
            title: "Utilisateur créé",
            description: "Le mail pour choisir son mot de passe est parti. Le lien est valable 24 heures.",
            variant: "success",
          })
        } else {
          toast({
            title: "Compte créé, mais le mail n'est pas parti",
            description: "Utilisez « Renvoyer l'invitation » dans la liste des utilisateurs. Si l'envoi échoue encore, prévenez la personne qui gère n8n.",
            variant: "destructive",
          })
        }
        setIsCreateUserModalOpen(false)
        setCreateUserForm({ username: '', is_admin: false })
        await loadUsers()
      } else {
        toast({
          title: "Erreur de création",
          description: response.error || "Erreur lors de la création de l'utilisateur",
          variant: "destructive",
        })
      }
    } catch (error) {
      console.error('Erreur lors de la création:', error)
      toast({
        title: "Erreur de connexion",
        description: "Impossible de créer l'utilisateur",
        variant: "destructive",
      })
    } finally {
      setSavingUser(false)
    }
  }

  const handleEditUser = async () => {
    if (!selectedUser || !editUserForm.username) {
      toast({
        title: "Erreur de validation",
        description: "L'adresse email est requise",
        variant: "destructive",
      })
      return
    }

    if (!isValidEmail(editUserForm.username)) {
      toast({
        title: "Erreur de validation",
        description: "Veuillez entrer une adresse email valide",
        variant: "destructive",
      })
      return
    }

    try {
      setSavingUser(true)
      
      // Mettre à jour les informations de l'utilisateur
      const userUpdateData: UserUpdateInput = {
        username: normalizeEmail(editUserForm.username),
        is_admin: editUserForm.is_admin
      }
      
      const response = await apiService.updateUser(selectedUser.id, userUpdateData)
      
      if (response.success) {
        // Si un nouveau mot de passe est fourni, le mettre à jour
        if (editUserForm.password && editUserForm.password.length >= 6) {
          const passwordResponse = await apiService.changeUserPassword(selectedUser.id, editUserForm.password)
          if (!passwordResponse.success) {
            toast({
              title: "Avertissement",
              description: "Utilisateur mis à jour mais erreur lors du changement de mot de passe",
              variant: "destructive",
            })
            return
          }
        }
        
        toast({
          title: "Utilisateur modifié",
          description: "L'utilisateur a été modifié avec succès",
          variant: "success",
        })
        setIsEditUserModalOpen(false)
        setSelectedUser(null)
        setEditUserForm({ username: '', is_admin: false, password: '' })
        await loadUsers()
      } else {
        toast({
          title: "Erreur de modification",
          description: response.error || "Erreur lors de la modification de l'utilisateur",
          variant: "destructive",
        })
      }
    } catch (error) {
      console.error('Erreur lors de la modification:', error)
      toast({
        title: "Erreur de connexion",
        description: "Impossible de modifier l'utilisateur",
        variant: "destructive",
      })
    } finally {
      setSavingUser(false)
    }
  }

  const handleDeleteUser = async () => {
    if (!selectedUser) return

    try {
      setDeletingUser(true)
      const response = await apiService.deleteUser(selectedUser.id)
      
      if (response.success) {
        toast({
          title: "Utilisateur supprimé",
          description: "L'utilisateur a été supprimé avec succès",
          variant: "success",
        })
        setIsDeleteUserModalOpen(false)
        setSelectedUser(null)
        await loadUsers()
      } else {
        toast({
          title: "Erreur de suppression",
          description: response.error || "Erreur lors de la suppression de l'utilisateur",
          variant: "destructive",
        })
      }
    } catch (error) {
      console.error('Erreur lors de la suppression:', error)
      toast({
        title: "Erreur de connexion",
        description: "Impossible de supprimer l'utilisateur",
        variant: "destructive",
      })
    } finally {
      setDeletingUser(false)
    }
  }

  const renvoyerInvitation = async (user: UserForList) => {
    setInvitationEnCours(user.id)
    try {
      const r = await apiService.renvoyerInvitationUtilisateur(user.id)
      if (r.success && r.data?.invitationEnvoyee) {
        toast({ title: "L'invitation a été renvoyée", description: `Un nouveau lien, valable 24 heures, est parti à ${user.username}. L'ancien ne fonctionne plus.`, variant: "success" })
      } else {
        toast({
          title: "L'invitation n'a pas pu être renvoyée",
          description: r.success ? "Le mail n'est pas parti. Prévenez la personne qui gère n8n." : r.error,
          variant: "destructive",
        })
      }
    } finally {
      setInvitationEnCours(null)
    }
  }

  const openEditModal = (user: UserForList) => {
    setSelectedUser(user)
    setEditUserForm({
      username: user.username,
      is_admin: user.is_admin,
      password: ''
    })
    setIsEditUserModalOpen(true)
  }

  const openDeleteModal = (user: UserForList) => {
    setSelectedUser(user)
    setIsDeleteUserModalOpen(true)
  }

  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString('fr-FR')
  }

  // Sauvegarder les paramètres
  const handleSaveSettings = async () => {
    try {
      setSaving(true)
      
      const settingsData = {
        elementsPerPageDashboard: parseInt(elementsPerPage.dashboard),
        elementsPerPageContacts: parseInt(elementsPerPage.contacts),
        elementsPerPageCompanies: parseInt(elementsPerPage.companies)
      }

      const response = await apiService.saveSettings(settingsData)

      if (response.success) {
        toast({
          title: "Paramètres sauvegardés",
          description: "Vos paramètres ont été enregistrés avec succès.",
          variant: "success",
        })
      } else {
        toast({
          title: "Erreur de sauvegarde",
          description: response.error || "Erreur lors de la sauvegarde des paramètres",
          variant: "destructive",
        })
      }
    } catch (error) {
      console.error('Erreur lors de la sauvegarde:', error)
      toast({
        title: "Erreur de connexion",
        description: "Impossible de sauvegarder les paramètres",
        variant: "destructive",
      })
    } finally {
      setSaving(false)
    }
  }

  if (loading) {
    return (
      <div className="space-y-6">
      <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
            <SettingsIcon className="w-6 h-6 text-blue-600" />
            <span>Paramètres</span>
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Gérez vos Paramètres pour contacts, entreprises, utilisateurs et affichage
          </p>
        </div>
        
        <div className="flex items-center justify-center py-20">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
        </div>
      </div>
    )
  }

  return (
    <div className="space-y-6">
      {/* Header */}
      <div>
          <h1 className="text-2xl font-bold text-gray-900 flex items-center space-x-2">
            <SettingsIcon className="w-6 h-6 text-blue-600" />
            <span>Paramètres</span>
          </h1>
          <p className="mt-1 text-sm text-gray-600">
            Gérez vos Paramètres pour contacts, entreprises, utilisateurs et affichage
          </p>
        </div>

      {/* Paramètres généraux */}
      <Card>
        <CardHeader>
          <CardTitle>Paramètres généraux</CardTitle>
          <CardDescription>
            Configurez les paramètres d'affichage de votre CRM
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-6">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-6">
            {/* Éléments par page - Dashboard */}
            <div className="space-y-2">
              <Label htmlFor="elements-dashboard">Éléments par page - Dashboard</Label>
              <Select 
                value={elementsPerPage.dashboard} 
                onValueChange={(value) => setElementsPerPage(prev => ({ ...prev, dashboard: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="3">3</SelectItem>
                  <SelectItem value="5">5</SelectItem>
                  <SelectItem value="7">7</SelectItem>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="15">15</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Éléments par page - Contacts */}
            <div className="space-y-2">
              <Label htmlFor="elements-contacts">Éléments par page - Contacts</Label>
              <Select 
                value={elementsPerPage.contacts} 
                onValueChange={(value) => setElementsPerPage(prev => ({ ...prev, contacts: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="15">15</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                </SelectContent>
              </Select>
            </div>

            {/* Éléments par page - Companies */}
            <div className="space-y-2">
              <Label htmlFor="elements-companies">Éléments par page - Entreprises</Label>
              <Select 
                value={elementsPerPage.companies} 
                onValueChange={(value) => setElementsPerPage(prev => ({ ...prev, companies: value }))}
              >
                <SelectTrigger>
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="10">10</SelectItem>
                  <SelectItem value="15">15</SelectItem>
                  <SelectItem value="20">20</SelectItem>
                  <SelectItem value="25">25</SelectItem>
                  <SelectItem value="50">50</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </div>

          {/* Bouton de sauvegarde */}
          <div className="flex justify-end pt-4">
            <Button 
              onClick={handleSaveSettings}
              disabled={saving}
              className="bg-blue-600 hover:bg-blue-700"
            >
              {saving ? (
                <>
                  <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
                  Enregistrement...
                </>
              ) : (
                <>
                  <Save className="w-4 h-4 mr-2" />
                  Enregistrer les paramètres
                </>
              )}
            </Button>
          </div>
        </CardContent>
      </Card>

      {/* Gestion des utilisateurs */}
      <Card>
        <CardHeader>
          <CardTitle className="flex items-center space-x-2">
            <Users className="w-5 h-5" />
            <span>Gestion des utilisateurs</span>
          </CardTitle>
          <CardDescription>
            Gérez les utilisateurs de votre CRM
          </CardDescription>
        </CardHeader>
        <CardContent className="space-y-4">
          {/* Bouton ajouter utilisateur */}
          <div className="flex justify-start">
            <Dialog open={isCreateUserModalOpen} onOpenChange={setIsCreateUserModalOpen}>
              <DialogTrigger asChild>
                <Button className="bg-blue-600 hover:bg-blue-700">
                  <UserPlus className="w-4 h-4 mr-2" />
                  Ajouter un utilisateur
                </Button>
              </DialogTrigger>
              <DialogContent>
                <DialogHeader>
                  <DialogTitle>Nouvel utilisateur</DialogTitle>
                </DialogHeader>
                <div className="space-y-4">
                  <div>
                    <Label htmlFor="username">Adresse email</Label>
                    <div className="relative">
                      <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                        <Mail className="h-4 w-4 text-gray-400" />
                      </div>
                      <Input
                        id="username"
                        type="email"
                        value={createUserForm.username}
                        onChange={(e) => setCreateUserForm(prev => ({ ...prev, username: e.target.value }))}
                        placeholder="utilisateur@exemple.com"
                        className="pl-10"
                      />
                    </div>
                    <p className="text-xs text-gray-500 mt-1">Un email de configuration du mot de passe sera envoyé à cette adresse</p>
                  </div>
                  <div className="flex items-center space-x-2">
                    <input
                      type="checkbox"
                      id="is_admin"
                      checked={createUserForm.is_admin}
                      onChange={(e) => setCreateUserForm(prev => ({ ...prev, is_admin: e.target.checked }))}
                      className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                    />
                    <Label htmlFor="is_admin" className="text-sm font-normal cursor-pointer">
                      Administrateur
                    </Label>
                  </div>
                </div>
                <DialogFooter>
                  <Button variant="outline" onClick={() => setIsCreateUserModalOpen(false)}>
                    Annuler
                  </Button>
                  <Button onClick={handleCreateUser} disabled={savingUser}>
                    {savingUser ? 'Création...' : 'Créer l\'utilisateur'}
                  </Button>
                </DialogFooter>
              </DialogContent>
            </Dialog>
          </div>

          {/* Tableau des utilisateurs */}
          <div className="border rounded-lg">
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead className="font-medium">ADRESSE EMAIL ↑</TableHead>
                  <TableHead className="font-medium">RÔLE ↑</TableHead>
                  <TableHead className="font-medium">DATE DE CRÉATION ↑</TableHead>
                  <TableHead className="font-medium">ACTIONS</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {loadingUsers ? (
                  // Squelettes de chargement
                  Array.from({ length: 3 }).map((_, index) => (
                    <TableRow key={index}>
                      <TableCell>
                        <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                      </TableCell>
                      <TableCell>
                        <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                      </TableCell>
                      <TableCell>
                        <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                      </TableCell>
                      <TableCell>
                        <div className="h-4 bg-gray-200 rounded animate-pulse"></div>
                      </TableCell>
                    </TableRow>
                  ))
                ) : users.length === 0 ? (
                  <TableRow>
                    <TableCell colSpan={4} className="text-center py-8 text-gray-500">
                      <Users className="w-12 h-12 mx-auto mb-4 text-gray-300" />
                      <p>Aucun utilisateur trouvé</p>
                    </TableCell>
                  </TableRow>
                ) : (
                  users.map((user) => (
                    <TableRow key={user.id} className="hover:bg-gray-50">
                      <TableCell className="font-medium">
                        <div className="flex items-center space-x-2">
                          <Mail className="w-4 h-4 text-gray-400" />
                          <span>{user.username}</span>
                        </div>
                      </TableCell>
                      <TableCell>
                        {user.is_admin ? (
                          <Badge className="bg-yellow-500 text-yellow-900 hover:bg-yellow-500/80">
                            Administrateur
                          </Badge>
                        ) : (
                          <Badge variant="secondary">
                            Utilisateur
                          </Badge>
                        )}
                        {Boolean(user.invitation_en_attente) && (
                          <Badge variant="outline" className="ml-2 border-orange-300 text-orange-700" title="Ce compte n'a pas encore choisi son mot de passe">
                            Invitation en attente
                          </Badge>
                        )}
                      </TableCell>
                      <TableCell>{formatDate(user.created_at)}</TableCell>
                      <TableCell>
                        <div className="flex items-center space-x-1">
                          {Boolean(user.invitation_en_attente) && (
                            <Button
                              variant="ghost"
                              size="sm"
                              className="h-8 w-8 p-0"
                              title="Renvoyer l'invitation"
                              disabled={invitationEnCours === user.id}
                              onClick={() => void renvoyerInvitation(user)}
                            >
                              <Send className="w-4 h-4 text-blue-600" />
                            </Button>
                          )}
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-8 w-8 p-0" 
                            title="Modifier"
                            onClick={() => openEditModal(user)}
                          >
                            <Edit className="w-4 h-4 text-green-600" />
                          </Button>
                          <Button 
                            variant="ghost" 
                            size="sm" 
                            className="h-8 w-8 p-0" 
                            title="Supprimer"
                            onClick={() => openDeleteModal(user)}
                          >
                            <Trash2 className="w-4 h-4 text-red-600" />
                          </Button>
                        </div>
                      </TableCell>
                    </TableRow>
                  ))
                )}
              </TableBody>
            </Table>
          </div>
        </CardContent>
      </Card>

      {/* Modal d'édition d'utilisateur */}
      <Dialog open={isEditUserModalOpen} onOpenChange={setIsEditUserModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Modifier l'utilisateur</DialogTitle>
          </DialogHeader>
          <div className="space-y-4">
            <div>
              <Label htmlFor="edit_username">Adresse email</Label>
              <div className="relative">
                <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                  <Mail className="h-4 w-4 text-gray-400" />
                </div>
                <Input
                  id="edit_username"
                  type="email"
                  value={editUserForm.username}
                  onChange={(e) => setEditUserForm(prev => ({ ...prev, username: e.target.value }))}
                  placeholder="utilisateur@exemple.com"
                  className="pl-10"
                />
              </div>
            </div>
            <div>
              <Label htmlFor="edit_password">Nouveau mot de passe (optionnel)</Label>
              <Input
                id="edit_password"
                type="password"
                value={editUserForm.password || ''}
                onChange={(e) => setEditUserForm(prev => ({ ...prev, password: e.target.value }))}
                placeholder="Nouveau mot de passe"
              />
            </div>
            <div className="flex items-center space-x-2">
              <input
                type="checkbox"
                id="edit_is_admin"
                checked={editUserForm.is_admin}
                onChange={(e) => setEditUserForm(prev => ({ ...prev, is_admin: e.target.checked }))}
                className="h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
              />
              <Label htmlFor="edit_is_admin" className="text-sm font-normal cursor-pointer">
                Administrateur
              </Label>
            </div>
          </div>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsEditUserModalOpen(false)}>
              Annuler
            </Button>
            <Button onClick={handleEditUser} disabled={savingUser}>
              {savingUser ? 'Mise à jour...' : 'Mettre à jour'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Modal de confirmation de suppression */}
      <Dialog open={isDeleteUserModalOpen} onOpenChange={setIsDeleteUserModalOpen}>
        <DialogContent>
          <DialogHeader>
            <DialogTitle>Supprimer l'utilisateur</DialogTitle>
            <DialogDescription>
              Êtes-vous sûr de vouloir supprimer l'utilisateur <strong>{selectedUser?.username}</strong> ? 
              Cette action est irréversible.
            </DialogDescription>
          </DialogHeader>
          <DialogFooter>
            <Button variant="outline" onClick={() => setIsDeleteUserModalOpen(false)}>
              Annuler
            </Button>
            <Button
              variant="destructive"
              onClick={handleDeleteUser}
              disabled={deletingUser}
            >
              {deletingUser ? 'Suppression...' : 'Supprimer'}
            </Button>
          </DialogFooter>
        </DialogContent>
      </Dialog>

      {/* Actions multiples */}
      <Card>
        <CardHeader>
          <div className="flex items-start justify-between gap-3">
            <div>
              <CardTitle className="flex items-center gap-2">
                <Edit3 className="w-5 h-5 text-blue-600" />
                <span>Actions multiples · contacts et entreprises</span>
              </CardTitle>
              <CardDescription>
                Modifier plusieurs champs d'un coup sur une liste de contacts ou
                d'entreprises. Importez la liste par copier-coller ou fichier
                (emails pour les contacts, noms ou SIRET pour les entreprises),
                puis empilez les modifications à appliquer.
              </CardDescription>
            </div>
          </div>
        </CardHeader>
        <CardContent>
          <Button
            onClick={() => setBulkOpen(true)}
            className="bg-blue-600 hover:bg-blue-700 text-white inline-flex items-center gap-2"
          >
            <Edit3 className="w-4 h-4" />
            Ouvrir « Actions multiples »
          </Button>
        </CardContent>
      </Card>

      {/* Clés d'API — accès en lecture seule pour les autres applications UP */}
      <ApiKeysCard />

      {/* Le connecteur IA (MCP), pour Claude et Copilot (28/10/2026) */}
      {estAdmin && <ConnecteurCard />}

      {/* Comptes UP-SPOT — UP-CRM en est la porte d'entrée (administrateurs seulement) */}
      {estAdmin && <UpSpotCard />}

      {/* Modal Actions multiples */}
      <BulkActionsModal open={bulkOpen} onOpenChange={setBulkOpen} />

      {/* Version de l'application */}
      <div className="pt-2 pb-4 text-center">
        <p className="text-xs text-gray-400">
          UP-CRM v{__APP_VERSION__} · build{' '}
          <span className="font-mono text-gray-500">{__APP_COMMIT__}</span>
        </p>
      </div>
    </div>
  )
}